import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config';

/**
 * SchemaBrowser — read-only database schema reference panel.
 *
 * Renders a collapsible list of tables from the connected database credential,
 * expands each to show columns (name + type). Click a column to copy
 * `tablename.column` to the clipboard as a discovery aid for SQL query
 * authoring. No two-way binding with the query field; reference-only.
 *
 * Backs onto the existing endpoints:
 *   GET /credentials/{id}/db-metadata/tables
 *   GET /credentials/{id}/db-metadata/columns?table_name={schema.table}
 *
 * Supported credential flavors: postgres, mysql, sqlserver, snowflake,
 * bigquery, databricks, neon (which covers Neon, PlanetScale, and Turso —
 * the backend sniffs the provider from the credential shape). Other
 * flavors get a 400 from the backend which the component surfaces verbatim.
 *
 * Props:
 *   credentialId — the credential id to introspect
 *   disabled     — boolean; when true, just shows a hint
 */
export default function SchemaBrowser({ credentialId, disabled = false }) {
  const [collapsed, setCollapsed] = useState(true);
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tablesError, setTablesError] = useState('');
  const [openTable, setOpenTable] = useState(null);
  const [columnsByTable, setColumnsByTable] = useState({});
  const [columnsLoading, setColumnsLoading] = useState('');
  const [columnsError, setColumnsError] = useState('');
  const [filter, setFilter] = useState('');
  const [copiedValue, setCopiedValue] = useState('');
  const copyTimer = useRef(null);

  const loadTables = useCallback(async () => {
    if (!credentialId) return;
    setTablesLoading(true);
    setTablesError('');
    try {
      const token = localStorage.getItem('token') || '';
      const resp = await fetch(
        `${API_BASE_URL}/credentials/${credentialId}/db-metadata/tables`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const mapped = (Array.isArray(data) ? data : []).map((item) => ({
        fullName: item.full_name || item.name,
        name: item.name,
        schema: item.schema || '',
      }));
      setTables(mapped);
    } catch (e) {
      setTablesError(e.message || 'Failed to load tables');
      setTables([]);
    } finally {
      setTablesLoading(false);
    }
  }, [credentialId]);

  const loadColumns = useCallback(async (tableFullName) => {
    if (!credentialId || !tableFullName) return;
    setColumnsLoading(tableFullName);
    setColumnsError('');
    try {
      const token = localStorage.getItem('token') || '';
      const qs = new URLSearchParams({ table_name: tableFullName });
      const resp = await fetch(
        `${API_BASE_URL}/credentials/${credentialId}/db-metadata/columns?${qs.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const mapped = (Array.isArray(data) ? data : []).map((c) => ({
        name: String(c.name || ''),
        type: String(c.type || ''),
      }));
      setColumnsByTable((prev) => ({ ...prev, [tableFullName]: mapped }));
    } catch (e) {
      setColumnsError(e.message || 'Failed to load columns');
    } finally {
      setColumnsLoading('');
    }
  }, [credentialId]);

  useEffect(() => {
    setCollapsed(true);
    setTables([]);
    setOpenTable(null);
    setColumnsByTable({});
    setFilter('');
    setTablesError('');
    setColumnsError('');
  }, [credentialId]);

  useEffect(() => {
    if (!collapsed && credentialId && tables.length === 0 && !tablesLoading && !tablesError) {
      loadTables();
    }
  }, [collapsed, credentialId, tables.length, tablesLoading, tablesError, loadTables]);

  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  const handleTableClick = (fullName) => {
    const next = openTable === fullName ? null : fullName;
    setOpenTable(next);
    if (next && !columnsByTable[next] && columnsLoading !== next) {
      loadColumns(next);
    }
  };

  const handleCopy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      setCopiedValue(text);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopiedValue(''), 1500);
    } catch (e) {  // eslint-disable-line no-unused-vars
      // Clipboard API blocked — silently ignore, user can still read the value
    }
  };

  const filteredTables = filter.trim()
    ? tables.filter((t) => t.fullName.toLowerCase().includes(filter.toLowerCase()))
    : tables;

  if (disabled || !credentialId) {
    return (
      <div style={panelStyle}>
        <div style={headerStyle}>
          <span style={titleStyle}>📚 Schema Browser</span>
          <span style={hintStyle}>-- Select a credential to browse tables --</span>
        </div>
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <div
        style={{ ...headerStyle, cursor: 'pointer' }}
        onClick={() => setCollapsed((c) => !c)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCollapsed((c) => !c); }
        }}
      >
        <span style={titleStyle}>📚 Schema Browser</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {!collapsed && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); loadTables(); }}
              disabled={tablesLoading}
              title="Reload tables"
              style={refreshBtnStyle(tablesLoading)}
            >
              {tablesLoading ? '…' : '↻'}
            </button>
          )}
          <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
            {collapsed ? '▼' : '▲'}
          </span>
        </span>
      </div>

      {!collapsed && (
        <div style={bodyStyle}>
          {tablesError ? (
            <div style={errorStyle}>
              {tablesError}
              <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                Schema browsing supports PostgreSQL, MySQL, SQL Server, Snowflake, BigQuery, Databricks, and Neon/PlanetScale/Turso credentials.
              </div>
            </div>
          ) : (
            <>
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={tablesLoading ? 'Loading tables…' : `Filter ${tables.length} table${tables.length === 1 ? '' : 's'}…`}
                disabled={tablesLoading || tables.length === 0}
                style={filterInputStyle}
              />
              <div style={listStyle}>
                {tablesLoading ? (
                  <div style={emptyStyle}>Loading…</div>
                ) : filteredTables.length === 0 ? (
                  <div style={emptyStyle}>{tables.length === 0 ? 'No tables found' : 'No matches'}</div>
                ) : (
                  filteredTables.map((t) => {
                    const isOpen = openTable === t.fullName;
                    const cols = columnsByTable[t.fullName];
                    const loadingThis = columnsLoading === t.fullName;
                    return (
                      <div key={t.fullName} style={tableRowContainerStyle}>
                        <div
                          style={tableRowStyle(isOpen)}
                          onClick={() => handleTableClick(t.fullName)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTableClick(t.fullName); }
                          }}
                          title="Click to show columns; click the copy icon to copy the table name"
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', width: '10px' }}>{isOpen ? '▾' : '▸'}</span>
                            <span style={tableNameStyle}>{t.fullName}</span>
                          </span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleCopy(t.fullName); }}
                            title={`Copy "${t.fullName}"`}
                            style={copyBtnStyle}
                          >
                            {copiedValue === t.fullName ? '✓' : '📋'}
                          </button>
                        </div>
                        {isOpen && (
                          <div style={columnsContainerStyle}>
                            {loadingThis ? (
                              <div style={columnHintStyle}>Loading columns…</div>
                            ) : columnsError && !cols ? (
                              <div style={columnErrorStyle}>{columnsError}</div>
                            ) : !cols || cols.length === 0 ? (
                              <div style={columnHintStyle}>No columns returned</div>
                            ) : (
                              cols.map((c) => {
                                const qualified = `${t.fullName}.${c.name}`;
                                return (
                                  <div
                                    key={c.name}
                                    style={columnRowStyle}
                                    onClick={() => handleCopy(qualified)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCopy(qualified); }
                                    }}
                                    title={`Click to copy "${qualified}"`}
                                  >
                                    <span style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                      <span style={columnNameStyle}>{c.name}</span>
                                      {c.type && <span style={columnTypeStyle}>{c.type}</span>}
                                    </span>
                                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', flexShrink: 0 }}>
                                      {copiedValue === qualified ? '✓ copied' : 'copy'}
                                    </span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const panelStyle = {
  background: 'var(--bg-tertiary)',
  border: '1px solid var(--border-color)',
  borderRadius: '4px',
  marginBottom: '15px',
  fontSize: '12px',
  overflow: 'hidden',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 10px',
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
};

const titleStyle = {
  fontWeight: 600,
  color: 'var(--text-primary)',
  fontSize: '12px',
};

const hintStyle = {
  fontSize: '11px',
  color: 'var(--text-tertiary)',
  fontStyle: 'italic',
};

const refreshBtnStyle = (loading) => ({
  padding: '2px 8px',
  background: 'var(--bg-tertiary)',
  border: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
  borderRadius: '3px',
  cursor: loading ? 'not-allowed' : 'pointer',
  fontSize: '11px',
  opacity: loading ? 0.6 : 1,
});

const bodyStyle = {
  padding: '8px',
};

const errorStyle = {
  padding: '8px',
  color: '#f87171',
  fontSize: '12px',
  background: 'rgba(248, 113, 113, 0.08)',
  border: '1px solid rgba(248, 113, 113, 0.3)',
  borderRadius: '3px',
};

const filterInputStyle = {
  width: '100%',
  padding: '5px 8px',
  background: 'var(--bg-input)',
  border: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
  borderRadius: '3px',
  fontSize: '12px',
  boxSizing: 'border-box',
  marginBottom: '6px',
};

const listStyle = {
  maxHeight: '260px',
  overflowY: 'auto',
};

const emptyStyle = {
  padding: '8px',
  fontSize: '12px',
  color: 'var(--text-tertiary)',
  fontStyle: 'italic',
  textAlign: 'center',
};

const tableRowContainerStyle = {
  marginBottom: '2px',
};

const tableRowStyle = (isOpen) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '5px 6px',
  cursor: 'pointer',
  background: isOpen ? 'var(--bg-hover, rgba(255,255,255,0.05))' : 'transparent',
  borderRadius: '3px',
  gap: '4px',
});

const tableNameStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '12px',
  color: 'var(--text-primary)',
  fontFamily: 'monospace',
};

const copyBtnStyle = {
  padding: '2px 5px',
  background: 'transparent',
  border: '1px solid transparent',
  color: 'var(--text-tertiary)',
  borderRadius: '3px',
  cursor: 'pointer',
  fontSize: '11px',
  flexShrink: 0,
};

const columnsContainerStyle = {
  marginLeft: '18px',
  marginTop: '2px',
  paddingLeft: '6px',
  borderLeft: '1px solid var(--border-color)',
};

const columnRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '3px 6px',
  cursor: 'pointer',
  borderRadius: '3px',
  gap: '6px',
};

const columnNameStyle = {
  fontFamily: 'monospace',
  fontSize: '11px',
  color: 'var(--text-primary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const columnTypeStyle = {
  fontFamily: 'monospace',
  fontSize: '11px',
  color: 'var(--text-tertiary)',
  fontStyle: 'italic',
};

const columnHintStyle = {
  padding: '4px 6px',
  fontSize: '11px',
  color: 'var(--text-tertiary)',
  fontStyle: 'italic',
};

const columnErrorStyle = {
  padding: '4px 6px',
  fontSize: '11px',
  color: '#f87171',
};
